import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { AuthShell } from "./Login";

export const RegisterForm = ({ inviteCode = null, initial = {}, submitLabel = "Create account" }) => {
  const [form, setForm] = useState({ name: initial.name || "", email: initial.email || "", password: "", agreed_to_terms: false, confirmed_adult: false });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.confirmed_adult) {
      setError("extras.tv membership is for people aged 18 or over. Please confirm you are 18 or over.");
      return;
    }
    if (!form.agreed_to_terms) {
      setError("Please tick the box to agree to the Terms & Conditions and the Membership & Data Protection Agreement.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/register", inviteCode ? { ...form, invite_code: inviteCode } : form);
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        <div>
          <label className="label-xs">Name (as shown on your profile)</label>
          <Input required value={form.name} onChange={set("name")} className="mt-2 h-12" data-testid="register-name-input" />
        </div>
        <div>
          <label className="label-xs">Email</label>
          <Input type="email" required value={form.email} onChange={set("email")} className="mt-2 h-12" data-testid="register-email-input" />
        </div>
        <div>
          <label className="label-xs">Password (min 8 characters)</label>
          <Input type="password" required minLength={8} value={form.password} onChange={set("password")} className="mt-2 h-12" data-testid="register-password-input" />
        </div>
        <label className="flex items-start gap-3 cursor-pointer text-sm text-white/75" data-testid="register-adult-label">
          <Checkbox checked={form.confirmed_adult} onCheckedChange={(v) => { setForm({ ...form, confirmed_adult: v === true }); if (v === true) setError(""); }} className="mt-0.5" data-testid="register-adult-checkbox" />
          <span>I confirm I am 18 years of age or over.</span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer text-sm text-white/75" data-testid="register-agreement-label">
          <Checkbox checked={form.agreed_to_terms} onCheckedChange={(v) => { setForm({ ...form, agreed_to_terms: v === true }); if (v === true) setError(""); }} className="mt-0.5" data-testid="register-agreement-checkbox" />
          <span>
            I agree to the{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid="register-terms-link">Terms & Conditions</a>
            {" "}and the{" "}
            <a href="/data-protection" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid="register-agreement-link">Membership & Data Protection Agreement</a>
            {" "}and have read the{" "}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid="register-privacy-link">Privacy Notice</a>.
          </span>
        </label>
        {error && <p className="text-sm text-red-400" data-testid="register-error">{error}</p>}
        <button disabled={busy} className="w-full bg-primary text-primary-foreground py-4 font-bold uppercase text-sm tracking-wider hover:bg-[hsl(43,100%,60%)] transition-colors disabled:opacity-60" data-testid="register-submit-button">
          {busy ? "Creating account…" : submitLabel}
        </button>
        <p className="text-sm text-white/60">
          Already a member? <Link to="/login" className="text-primary hover:underline" data-testid="register-login-link">Log in</Link>
        </p>
      </form>
  );
};

export default function Register() {
  return (
    <AuthShell title="Get listed" subtitle="Create your own web address (extras.tv/yourname), add your photos and a few words about yourself, then share it in Facebook groups and beyond. First 6 months FREE, then £10 / $14 / €12 a year. Members must be 18 or over.">
      <RegisterForm />
    </AuthShell>
  );
}
